/**
 * GoogleMapsProvider.tsx
 *
 * Shared Google Maps script loader provider.
 * Loads the Google Maps JavaScript API once at the app level
 * to avoid conflicts from multiple LoadScript components.
 *
 * Usage:
 *   // In App.tsx or main.tsx:
 *   <GoogleMapsProvider>
 *     <App />
 *   </GoogleMapsProvider>
 *
 *   // In any component:
 *   const { isLoaded, loadError } = useGoogleMaps();
 *   if (!isLoaded) return <Loading />;
 *   if (loadError) return <Error />;
 *   return <GoogleMap ... />;
 */

import { createContext, useContext, type ReactNode } from 'react';
import { useJsApiLoader, type Libraries } from '@react-google-maps/api';

interface GoogleMapsContextValue {
  isLoaded: boolean;
  loadError: Error | undefined;
}

const GoogleMapsContext = createContext<GoogleMapsContextValue>({
  isLoaded: false,
  loadError: undefined,
});

// Libraries to load - defined outside component to prevent re-renders
// 'visualization' library is required for HeatmapLayer (used in PermitHeatmapLayer)
const libraries: Libraries = ['places', 'visualization'];

interface GoogleMapsProviderProps {
  children: ReactNode;
}

export function GoogleMapsProvider({ children }: GoogleMapsProviderProps) {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey,
    libraries,
  });

  return (
    <GoogleMapsContext.Provider value={{ isLoaded, loadError }}>
      {children}
    </GoogleMapsContext.Provider>
  );
}

/**
 * Hook to access Google Maps loading state.
 * Must be used within a GoogleMapsProvider.
 */
export function useGoogleMaps(): GoogleMapsContextValue {
  const context = useContext(GoogleMapsContext);
  if (context === undefined) {
    throw new Error('useGoogleMaps must be used within a GoogleMapsProvider');
  }
  return context;
}

export default GoogleMapsProvider;
